import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../services/apiService';
import { Company, SubscriptionPlan, User, UserRole, PlanConfig, CurrencyConfig } from '../types';
import AddCompanyModal from '../components/AddCompanyModal';
import EditCompanyModal from '../components/EditCompanyModal';
import AddUserModal from '../components/AddUserModal';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, AreaChart, Area } from 'recharts';

// --- Composant Audit Log ---
const AuditLogView: React.FC = () => {
  const [logs, setLogs] = useState<Array<{ id: number; user_name: string; user_role: string; action: string; target_type: string; target_id: string; details: string; ip_address: string; created_at: string }>>([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [filterAction, setFilterAction] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const data = await api.getAuditLogs({ page, limit: 30, filterAction: filterAction || undefined, filterUser: filterUser || undefined });
      setLogs(data.logs || []);
      setPages(data.pages || 0);
      setTotal(data.total || 0);
      setLoading(false);
    };
    load();
  }, [page, filterAction, filterUser]);

  const actionLabels: Record<string, string> = {
    create_company: 'Création entreprise', update_company_status: 'Statut entreprise',
    create_user: 'Création utilisateur', delete_user: 'Suppression utilisateur',
    bulk_update_leads: 'MAJ masse prospects', bulk_delete_leads: 'Suppression masse prospects',
    update_profile: 'Modification profil',
  };

  const actionColor = (a: string) => {
    if (a.includes('delete') || a.includes('bulk_delete')) return 'text-rose-400 bg-rose-500/10';
    if (a.includes('create')) return 'text-emerald-400 bg-emerald-500/10';
    if (a.includes('update') || a.includes('status')) return 'text-amber-400 bg-amber-500/10';
    return 'text-slate-400 bg-slate-500/10';
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-20">
      <div className="orion-admin-card p-8 rounded-2xl">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h3 className="text-2xl font-black text-[#e0eaff] mb-1">Journal d'audit</h3>
            <p className="text-[#7f93bc] text-sm">{total} entrée{total > 1 ? 's' : ''} enregistrée{total > 1 ? 's' : ''}</p>
          </div>
          <div className="flex items-center gap-3">
            <input type="text" placeholder="Filtrer par action..." value={filterAction} onChange={e => { setFilterAction(e.target.value); setPage(1); }} className="orion-lead-select text-xs px-3 py-2 rounded-xl w-44" />
            <input type="text" placeholder="Filtrer par utilisateur..." value={filterUser} onChange={e => { setFilterUser(e.target.value); setPage(1); }} className="orion-lead-select text-xs px-3 py-2 rounded-xl w-44" />
          </div>
        </div>
        {loading ? (
          <div className="text-center py-16 text-[#546a96] text-sm">Chargement...</div>
        ) : logs.length === 0 ? (
          <div className="text-center py-16 text-[#546a96] text-sm">Aucune entrée dans le journal</div>
        ) : (
          <div className="space-y-2">
            {logs.map(log => (
              <div key={log.id} className="flex items-center gap-4 px-4 py-3 rounded-xl bg-[rgba(14,28,58,0.4)] border border-[rgba(100,140,210,0.08)] hover:border-[rgba(100,140,210,0.2)] transition-colors">
                <div className="w-16 flex-shrink-0">
                  <span className={`text-[10px] font-black uppercase px-2 py-1 rounded-lg ${actionColor(log.action)}`}>
                    {log.action.includes('delete') ? 'DEL' : log.action.includes('create') ? 'NEW' : 'UPD'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-[#e0eaff]">{actionLabels[log.action] || log.action}</span>
                    {log.target_type && <span className="text-[10px] text-[#546a96] font-bold">({log.target_type})</span>}
                  </div>
                  <div className="text-[11px] text-[#546a96] truncate">{log.details || log.target_id || '-'}</div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-xs font-bold text-[#7f93bc]">{log.user_name || 'Système'}</div>
                  <div className="text-[10px] text-[#3d5280]">{log.user_role}</div>
                </div>
                <div className="text-right flex-shrink-0 w-28">
                  <div className="text-[11px] text-[#546a96]">{new Date(log.created_at).toLocaleDateString('fr-FR')}</div>
                  <div className="text-[10px] text-[#3d5280]">{new Date(log.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</div>
                </div>
                <div className="text-[10px] text-[#3d5280] w-24 text-right flex-shrink-0">{log.ip_address || '-'}</div>
              </div>
            ))}
          </div>
        )}
        {pages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="orion-lead-select px-3 py-1.5 rounded-lg text-xs font-bold disabled:opacity-30">Préc.</button>
            <span className="text-xs font-bold text-[#7f93bc]">{page} / {pages}</span>
            <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages} className="orion-lead-select px-3 py-1.5 rounded-lg text-xs font-bold disabled:opacity-30">Suiv.</button>
          </div>
        )}
      </div>
    </div>
  );
};

interface SuperAdminDashboardProps {
  activeView: string;
  onImpersonate: (user: User) => void;
  currentUser: User;
}

const SuperAdminDashboard: React.FC<SuperAdminDashboardProps> = ({ activeView, onImpersonate, currentUser }) => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [systemUsers, setSystemUsers] = useState<User[]>([]);
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [showAddSystemUser, setShowAddSystemUser] = useState(false);
  const [loading, setLoading] = useState(true);

  const [plans, setPlans] = useState<PlanConfig[]>([]);
  const [currencies, setCurrencies] = useState<CurrencyConfig[]>([]);

  const [editingPlan, setEditingPlan] = useState<PlanConfig | null>(null);
  const [isEditingPlan, setIsEditingPlan] = useState(false);

  const [newCurrency, setNewCurrency] = useState<CurrencyConfig>({ code: '', name: '', symbol: '' });

  // État édition membre équipe
  const [editingSystemUser, setEditingSystemUser] = useState<(User & { password?: string }) | null>(null);

  const fetchData = async () => {
    setLoading(true);
    const [c, u, config] = await Promise.all([
        api.getCompanies(),
        api.getSuperAdmins(),
        api.getSystemConfig()
    ]);
    setCompanies(c || []);
    setSystemUsers(u || []);
    setPlans(config.plans);
    setCurrencies(config.currencies);
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const toggleStatus = async (company: Company) => {
    if (currentUser.role !== UserRole.SUPER_ADMIN) {
        alert("Action refusée. Seul un Super Administrateur peut suspendre un client.");
        return;
    }
    const newStatus = company.status === 'active' ? 'suspended' : 'active';
    setCompanies(prev => prev.map(c => c.id === company.id ? { ...c, status: newStatus } : c));
    await api.updateCompanyStatus(company.id, newStatus);
  };

  const handleDeleteCompany = (id: string) => {
      if (currentUser.role !== UserRole.SUPER_ADMIN) {
        alert("Action refusée. Seul un Super Administrateur peut supprimer un client.");
        return;
    }
    const company = companies.find(c => c.id === id);
    const name = company?.name || '';
    const input = window.prompt(`Cette action est IRRÉVERSIBLE.\n\nPour confirmer la suppression, tapez le nom de l'entreprise :\n« ${name} »`);
    if (input !== null && input.trim() === name.trim()) {
        setCompanies(prev => prev.filter(c => c.id !== id));
    } else if (input !== null) {
        alert('Le nom ne correspond pas. Suppression annulée.');
    }
  };

  const handleCreateCompany = (data: any) => {
    const newId = 'c_' + Date.now() + Math.random().toString(36).substr(2, 5);
    const optimisticCompany: Company = {
        id: newId,
        name: data.name,
        plan: data.plan || 'BASIC',
        userLimit: data.userLimit || 3,
        activeLeads: 0,
        status: 'active',
        createdAt: new Date().toISOString(),
        currency: 'EUR'
    };

    setCompanies(prev => [optimisticCompany, ...prev]);

    api.createCompany({ ...data, id: newId }).catch(err => {
        console.error("Background sync failed", err);
    });
  };

  const handleUpdateCompany = (updates: Partial<Company>) => {
      if (!editingCompany) return;
      const targetId = editingCompany.id;

      const updatedCompany = { ...editingCompany, ...updates };
      setCompanies(prev => prev.map(c => c.id === targetId ? updatedCompany : c));

      api.updateCompany(targetId, updates).catch(err => {
          console.error("Background update failed", err);
      });
  };

  const handleCreateSystemUser = async (data: any) => {
     const newUser = await api.createUser({
         ...data,
         companyId: 'system'
     });
     setSystemUsers(prev => [...prev, newUser]);
  };

  const handleUpdateSystemUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingSystemUser) return;
    try {
      const updates: any = {
        name: editingSystemUser.name,
        email: editingSystemUser.email,
        role: editingSystemUser.role,
      };
      if (editingSystemUser.password) {
        updates.password = editingSystemUser.password;
      }
      await api.updateUser(editingSystemUser.id, updates);
      setSystemUsers(prev => prev.map(u => u.id === editingSystemUser.id ? { ...u, name: editingSystemUser.name, email: editingSystemUser.email, role: editingSystemUser.role } : u));
      setEditingSystemUser(null);
    } catch (err) {
      console.error(err);
      alert("Erreur lors de la mise à jour.");
    }
  };

  const handleDeleteSystemUser = async (userId: string) => {
    if (userId === currentUser.id) {
      alert("Vous ne pouvez pas supprimer votre propre compte.");
      return;
    }
    if (!confirm("Supprimer définitivement ce membre ?")) return;
    try {
      await api.deleteUser(userId);
      setSystemUsers(prev => prev.filter(u => u.id !== userId));
    } catch (err) {
      console.error(err);
      alert("Erreur lors de la suppression.");
    }
  };

  const impersonateCompany = (targetCompany: Company) => {
    const virtualAdmin: User = {
        id: 'impersonate_' + targetCompany.id + '_' + Date.now(),
        companyId: targetCompany.id,
        name: `Admin ${targetCompany.name}`,
        email: `admin@${targetCompany.name.toLowerCase().replace(/[^a-z0-9]/g, '')}.com`,
        role: UserRole.COMPANY_ADMIN,
        avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${targetCompany.id}`
    };
    onImpersonate(virtualAdmin);
  };

  // --- GESTION PLANS ---
  const handleSavePlan = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!editingPlan) return;

      let newPlans = [...plans];
      const existingIndex = plans.findIndex(p => p.id === editingPlan.id);

      if (existingIndex >= 0) {
          newPlans[existingIndex] = editingPlan;
      } else {
          newPlans = [...plans, editingPlan];
      }

      setPlans(newPlans);
      await api.updateSystemConfig({ plans: newPlans });

      setIsEditingPlan(false);
      setEditingPlan(null);
  };

  const startEditPlan = (plan: PlanConfig) => {
      setEditingPlan({...plan});
      setIsEditingPlan(true);
  };

  const startNewPlan = () => {
      setEditingPlan({
          id: '',
          label: '',
          priceMonthly: 0,
          priceYearly: 0,
          userLimit: 1
      });
      setIsEditingPlan(true);
  };

  const deletePlan = async (id: string) => {
      if(confirm('Supprimer ce plan ?')) {
          const newPlans = plans.filter(p => p.id !== id);
          setPlans(newPlans);
          await api.updateSystemConfig({ plans: newPlans });
      }
  };

  // --- GESTION DEVISES ---
  const addCurrency = async () => {
      if (!newCurrency.code || !newCurrency.name || !newCurrency.symbol) return;
      if (currencies.find(c => c.code === newCurrency.code)) {
          alert("Ce code devise existe déjà.");
          return;
      }
      const newCurrencies = [...currencies, newCurrency];
      setCurrencies(newCurrencies);
      await api.updateSystemConfig({ currencies: newCurrencies });
      setNewCurrency({ code: '', name: '', symbol: '' });
  };

  const removeCurrency = async (code: string) => {
      const newCurrencies = currencies.filter(c => c.code !== code);
      setCurrencies(newCurrencies);
      await api.updateSystemConfig({ currencies: newCurrencies });
  };


  // --- GRAPHIQUES ---
  const planData = useMemo(() => {
    const counts: {[key: string]: number} = {};
    plans.forEach(p => counts[p.id] = 0);
    companies.forEach(c => {
        const planId = c.plan || 'BASIC';
        counts[planId] = (counts[planId] || 0) + 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [companies, plans]);

  const COLORS = ['#94a3b8', '#6366f1', '#10b981', '#f59e0b', '#ef4444'];

  const totalMRR = companies.reduce((acc, c) => {
      const plan = plans.find(p => p.id === c.plan);
      return acc + (plan ? plan.priceMonthly : 0);
  }, 0);

  const revenueData = [
    { name: 'Jan', revenue: totalMRR * 0.8 }, { name: 'Fév', revenue: totalMRR * 0.85 },
    { name: 'Mar', revenue: totalMRR * 0.9 }, { name: 'Avr', revenue: totalMRR * 0.95 },
    { name: 'Mai', revenue: totalMRR }, { name: 'Juin', revenue: totalMRR * 1.1 },
  ];

  const chartTooltipStyle = {
    borderRadius: '12px',
    border: '1px solid rgba(100,140,210,0.25)',
    boxShadow: '0 8px 30px rgba(0,0,0,0.4)',
    background: 'rgba(14,28,58,0.95)',
    color: '#e0eaff',
    fontSize: '12px',
    fontWeight: 700,
  };

  // --- VUE DASHBOARD ---
  if (activeView === 'dashboard') {
      return (
        <div className="space-y-8 animate-in fade-in duration-500">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="orion-admin-card p-6 rounded-2xl">
                <div className="text-[10px] font-black uppercase tracking-widest mb-2 text-[#7f93bc]">Revenu Mensuel (MRR)</div>
                <div className="text-4xl font-black text-[#e0eaff]">{totalMRR.toLocaleString()} €</div>
                <div className="text-xs font-bold text-emerald-400 mt-2 flex items-center gap-1">
                    <span className="bg-emerald-500/20 text-emerald-300 px-1.5 py-0.5 rounded text-[9px] font-black">+12%</span>
                </div>
              </div>
              <div className="orion-admin-card p-6 rounded-2xl">
                <div className="text-[10px] font-black uppercase tracking-widest mb-2 text-[#7f93bc]">Clients Actifs</div>
                <div className="text-4xl font-black text-cyan-300">{companies.filter(c => c.status === 'active').length}</div>
              </div>
              <div className="orion-admin-card p-6 rounded-2xl">
                <div className="text-[10px] font-black uppercase tracking-widest mb-2 text-[#7f93bc]">Licences Utilisées</div>
                <div className="text-4xl font-black text-[#e0eaff]">{companies.reduce((acc, c) => acc + c.userLimit, 0)}</div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="orion-admin-card p-8 rounded-2xl">
                    <h3 className="text-lg font-black text-[#e0eaff] mb-6">Répartition par Plan</h3>
                    <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={planData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                                    {planData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="none" />
                                    ))}
                                </Pie>
                                <Tooltip contentStyle={chartTooltipStyle} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="orion-admin-card p-8 rounded-2xl">
                    <h3 className="text-lg font-black text-[#e0eaff] mb-6">Croissance du Revenu</h3>
                    <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={revenueData}>
                                <defs>
                                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(100,140,210,0.15)" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#7f93bc', fontSize: 12 }} />
                                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#7f93bc', fontSize: 12 }} />
                                <Tooltip contentStyle={chartTooltipStyle} />
                                <Area type="monotone" dataKey="revenue" stroke="#6366f1" strokeWidth={3} fillOpacity={1} fill="url(#colorRevenue)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
      );
  }

  // --- VUE CLIENTS ---
  if (activeView === 'companies') {
      return (
        <div className="space-y-6 animate-in fade-in duration-500">
            <div className="orion-admin-card rounded-2xl overflow-hidden min-h-[500px]">
              <div className="p-8 border-b border-[rgba(100,140,210,0.2)] flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-black text-[#e0eaff] tracking-tight">Portefeuille Clients</h2>
                    <p className="text-xs text-[#7f93bc] font-medium mt-1">{companies.length} environnements déployés</p>
                </div>
                <button
                    onClick={() => setShowAddCompany(true)}
                    className="orion-primary-btn text-white px-6 py-3 rounded-xl text-xs font-black flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
                  NOUVEAU CLIENT
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="text-[#7f93bc] text-[10px] font-black uppercase tracking-widest border-b border-[rgba(100,140,210,0.15)]">
                      <th className="px-8 py-5">Entreprise</th>
                      <th className="px-8 py-5">Plan</th>
                      <th className="px-8 py-5">Licences</th>
                      <th className="px-8 py-5">Devise</th>
                      <th className="px-8 py-5">Status</th>
                      <th className="px-8 py-5 text-right">Actions Support</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[rgba(100,140,210,0.1)]">
                    {companies.map((company) => (
                      <tr key={company.id} className="hover:bg-[rgba(30,55,110,0.3)] transition-colors group">
                        <td className="px-8 py-6">
                          <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl bg-indigo-500/20 text-indigo-300 flex items-center justify-center font-black text-sm border border-indigo-500/20">
                                  {company.name.charAt(0)}
                              </div>
                              <div>
                                <div className="font-black text-[#e0eaff]">{company.name}</div>
                                <div className="text-[10px] text-[#546a96] font-mono">ID: {company.id}</div>
                              </div>
                          </div>
                        </td>
                        <td className="px-8 py-6">
                          <span className="px-2.5 py-1 rounded-lg text-[9px] font-black uppercase border bg-[rgba(15,30,60,0.6)] text-[#7f93bc] border-[rgba(100,140,210,0.2)]">
                            {plans.find(p => p.id === company.plan)?.label || company.plan}
                          </span>
                        </td>
                        <td className="px-8 py-6">
                          <div className="text-xs font-bold text-[#95a7cc]">{company.userLimit} utilisateurs</div>
                        </td>
                        <td className="px-8 py-6">
                           <div className="text-xs font-bold text-[#7f93bc]">{company.currency || 'EUR'}</div>
                        </td>
                        <td className="px-8 py-6">
                            <button
                                onClick={() => toggleStatus(company)}
                                disabled={currentUser.role !== UserRole.SUPER_ADMIN}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${company.status === 'active' ? 'bg-emerald-500' : 'bg-rose-400'} ${currentUser.role !== UserRole.SUPER_ADMIN ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                            >
                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${company.status === 'active' ? 'translate-x-6' : 'translate-x-1'}`} />
                            </button>
                        </td>
                        <td className="px-8 py-6 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                                onClick={() => setEditingCompany(company)}
                                className="p-2 text-[#546a96] hover:text-cyan-300 hover:bg-cyan-500/10 rounded-lg transition-colors border border-transparent hover:border-cyan-500/20"
                                title="Modifier Abonnement"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                            </button>
                            <button
                                onClick={() => impersonateCompany(company)}
                                className="orion-primary-btn text-white px-3 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest flex items-center gap-1"
                            >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" /></svg>
                                Accéder
                            </button>
                            {currentUser.role === UserRole.SUPER_ADMIN && (
                                <button
                                    onClick={() => handleDeleteCompany(company.id)}
                                    className="p-2 text-rose-400/50 hover:text-rose-400 hover:bg-rose-500/10 rounded-lg transition-colors"
                                    title="Supprimer Client"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {showAddCompany && <AddCompanyModal onClose={() => setShowAddCompany(false)} onSubmit={handleCreateCompany} />}
            {editingCompany && <EditCompanyModal company={editingCompany} onClose={() => setEditingCompany(null)} onSubmit={handleUpdateCompany} />}
        </div>
      );
  }

  // --- VUE TEAM ---
  if (activeView === 'team') {
      return (
          <div className="space-y-6 animate-in fade-in duration-500">
             <div className="orion-admin-card rounded-2xl overflow-hidden min-h-[500px]">
             <div className="p-8 border-b border-[rgba(100,140,210,0.2)] flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-black text-[#e0eaff] tracking-tight">Équipe Interne</h2>
                    <p className="text-xs text-[#7f93bc] font-medium mt-1">Gestion des Super Admins et de l'équipe Support.</p>
                </div>
                {currentUser.role === UserRole.SUPER_ADMIN && (
                    <button
                        onClick={() => setShowAddSystemUser(true)}
                        className="orion-primary-btn text-white px-6 py-3 rounded-xl text-xs font-black"
                    >
                    + AJOUTER UN MEMBRE
                    </button>
                )}
             </div>
             <table className="w-full text-left">
                  <thead>
                    <tr className="text-[#7f93bc] text-[10px] font-black uppercase tracking-widest border-b border-[rgba(100,140,210,0.15)]">
                      <th className="px-8 py-5">Identité</th>
                      <th className="px-8 py-5">Rôle Système</th>
                      <th className="px-8 py-5">Permissions</th>
                      <th className="px-8 py-5 text-center">Statut</th>
                      {currentUser.role === UserRole.SUPER_ADMIN && (
                        <th className="px-8 py-5 text-right">Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[rgba(100,140,210,0.1)]">
                    {systemUsers.map((admin) => (
                      <tr key={admin.id} className="hover:bg-[rgba(30,55,110,0.3)] transition-colors group">
                        <td className="px-8 py-6">
                            <div className="flex items-center gap-4">
                                <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${admin.email}`} className="w-10 h-10 rounded-xl bg-[rgba(15,30,60,0.6)] border border-[rgba(100,140,210,0.2)]" />
                                <div>
                                    <div className="font-black text-[#e0eaff]">{admin.name}</div>
                                    <div className="text-xs text-[#7f93bc]">{admin.email}</div>
                                </div>
                            </div>
                        </td>
                        <td className="px-8 py-6">
                           <span className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase border ${admin.role === UserRole.SUPER_ADMIN ? 'bg-indigo-500/15 text-indigo-300 border-indigo-500/25' : 'bg-amber-500/15 text-amber-300 border-amber-500/25'}`}>
                               {admin.role === UserRole.SUPER_ADMIN ? 'Super Admin' : 'Support User'}
                           </span>
                        </td>
                        <td className="px-8 py-6">
                            <div className="text-xs text-[#7f93bc] font-medium max-w-xs">
                                {admin.role === UserRole.SUPER_ADMIN
                                    ? "Accès total, suspension clients, suppression."
                                    : "Accès CRM clients (impersonate), lecture seule config."}
                            </div>
                        </td>
                        <td className="px-8 py-6 text-center">
                           <span className="text-xs font-bold text-emerald-400 inline-flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                                Actif
                           </span>
                        </td>
                        {currentUser.role === UserRole.SUPER_ADMIN && (
                          <td className="px-8 py-6 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => setEditingSystemUser({ ...admin, password: '' })}
                                className="p-2 text-[#546a96] hover:text-cyan-300 hover:bg-cyan-500/10 rounded-lg transition-colors border border-transparent hover:border-cyan-500/20"
                                title="Modifier"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                              </button>
                              {admin.id !== currentUser.id && (
                                <button
                                  onClick={() => handleDeleteSystemUser(admin.id)}
                                  className="p-2 text-rose-400/50 hover:text-rose-400 hover:bg-rose-500/10 rounded-lg transition-colors"
                                  title="Supprimer"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
             </table>
           </div>

           {/* MODALE ÉDITION MEMBRE */}
           {editingSystemUser && (
             <div className="orion-modal-backdrop fixed inset-0 z-[150] flex items-center justify-center p-6">
               <div className="orion-modal-panel rounded-[2rem] p-8 w-full max-w-md shadow-2xl border border-[rgba(100,140,210,0.25)]">
                 <div className="flex items-center gap-4 mb-6">
                   <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${editingSystemUser.email}`} alt="Avatar" className="w-12 h-12 rounded-xl bg-[rgba(15,30,60,0.6)] border border-[rgba(100,140,210,0.2)]" />
                   <div>
                     <h3 className="text-xl font-black text-[#e0eaff]">Modifier le membre</h3>
                     <p className="text-xs text-[#546a96] font-mono">ID: {editingSystemUser.id}</p>
                   </div>
                 </div>
                 <form onSubmit={handleUpdateSystemUser} className="space-y-4">
                   <div>
                     <label htmlFor="edit-sys-name" className="text-[10px] font-bold text-[#7f93bc] uppercase tracking-widest">Nom complet</label>
                     <input
                       id="edit-sys-name"
                       required
                       type="text"
                       value={editingSystemUser.name}
                       onChange={e => setEditingSystemUser({ ...editingSystemUser, name: e.target.value })}
                       className="orion-admin-input w-full mt-1"
                       placeholder="Nom complet"
                     />
                   </div>
                   <div>
                     <label htmlFor="edit-sys-email" className="text-[10px] font-bold text-[#7f93bc] uppercase tracking-widest">Email</label>
                     <input
                       id="edit-sys-email"
                       required
                       type="email"
                       value={editingSystemUser.email}
                       onChange={e => setEditingSystemUser({ ...editingSystemUser, email: e.target.value })}
                       className="orion-admin-input w-full mt-1"
                       placeholder="email@exemple.com"
                     />
                   </div>
                   <div>
                     <label htmlFor="edit-sys-role" className="text-[10px] font-bold text-[#7f93bc] uppercase tracking-widest">Rôle</label>
                     <select
                       id="edit-sys-role"
                       title="Rôle système"
                       value={editingSystemUser.role}
                       onChange={e => setEditingSystemUser({ ...editingSystemUser, role: e.target.value as UserRole })}
                       className="orion-admin-input w-full mt-1"
                     >
                       <option value="SUPER_ADMIN">Super Admin</option>
                       <option value="COMPANY_ADMIN">Company Admin</option>
                       <option value="USER">User</option>
                     </select>
                   </div>
                   <div>
                     <label className="text-[10px] font-bold text-[#7f93bc] uppercase tracking-widest">Nouveau mot de passe <span className="text-[#546a96] normal-case">(laisser vide pour ne pas changer)</span></label>
                     <input
                       type="password"
                       value={editingSystemUser.password || ''}
                       onChange={e => setEditingSystemUser({ ...editingSystemUser, password: e.target.value })}
                       className="orion-admin-input w-full mt-1"
                       placeholder="••••••••"
                     />
                   </div>
                   <div className="flex gap-3 pt-4">
                     <button type="button" onClick={() => setEditingSystemUser(null)} className="flex-1 py-3 text-xs font-bold text-[#7f93bc] hover:text-[#e0eaff] transition-colors rounded-xl">Annuler</button>
                     <button type="submit" className="orion-primary-btn flex-1 py-3 text-white rounded-xl text-xs font-bold">Sauvegarder</button>
                   </div>
                 </form>
               </div>
             </div>
           )}

           {showAddSystemUser && <AddUserModal companyId="system" onClose={() => setShowAddSystemUser(false)} onSubmit={handleCreateSystemUser} />}
          </div>
      );
  }

  // --- VUE AUDIT LOG ---
  if (activeView === 'audit') {
    return <AuditLogView />;
  }

  // --- VUE PARAMÈTRES GLOBAUX ---
  if (activeView === 'settings') {
      return (
        <div className="space-y-8 animate-in fade-in duration-500 pb-20">
            {/* GESTION DES PLANS */}
            <div className="orion-admin-card p-10 rounded-2xl">
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <h3 className="text-2xl font-black text-[#e0eaff] mb-2">Plans & Tarification</h3>
                        <p className="text-[#7f93bc] text-sm">Définissez les offres commerciales (Mensuel / Annuel).</p>
                    </div>
                    {currentUser.role === UserRole.SUPER_ADMIN && (
                        <button onClick={startNewPlan} className="orion-primary-btn text-white px-5 py-2.5 rounded-xl text-xs font-black">
                            + Nouveau Plan
                        </button>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {plans.map((plan) => (
                        <div key={plan.id} className="p-6 rounded-2xl border border-[rgba(100,140,210,0.2)] bg-[rgba(15,30,60,0.5)] hover:bg-[rgba(20,40,80,0.6)] hover:border-[rgba(100,160,230,0.35)] transition-all group relative">
                            {currentUser.role === UserRole.SUPER_ADMIN && (
                                <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button onClick={() => startEditPlan(plan)} className="p-1.5 bg-[rgba(15,30,60,0.8)] rounded-lg border border-[rgba(100,140,210,0.25)] text-[#7f93bc] hover:text-cyan-300 transition-colors">✎</button>
                                    <button onClick={() => deletePlan(plan.id)} className="p-1.5 bg-[rgba(15,30,60,0.8)] rounded-lg border border-[rgba(100,140,210,0.25)] text-[#7f93bc] hover:text-rose-400 transition-colors">×</button>
                                </div>
                            )}
                            <div className="text-xs font-black text-[#7f93bc] uppercase tracking-widest mb-4">{plan.label} <span className="text-[9px] bg-[rgba(100,140,210,0.15)] text-[#546a96] px-1.5 py-0.5 rounded ml-1">{plan.id}</span></div>
                            <div className="space-y-4">
                                <div className="flex justify-between items-end border-b border-[rgba(100,140,210,0.15)] pb-2">
                                    <span className="text-[10px] font-bold text-[#546a96] uppercase">Mensuel</span>
                                    <span className="font-black text-[#e0eaff]">{plan.priceMonthly} €</span>
                                </div>
                                <div className="flex justify-between items-end border-b border-[rgba(100,140,210,0.15)] pb-2">
                                    <span className="text-[10px] font-bold text-[#546a96] uppercase">Annuel</span>
                                    <span className="font-black text-[#e0eaff]">{plan.priceYearly} €</span>
                                </div>
                                <div className="flex justify-between items-end">
                                    <span className="text-[10px] font-bold text-[#546a96] uppercase">Utilisateurs</span>
                                    <span className="font-black text-cyan-300">{plan.userLimit}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* GESTION DES DEVISES */}
            <div className="orion-admin-card p-10 rounded-2xl">
                <h3 className="text-2xl font-black text-[#e0eaff] mb-2">Devises & Système</h3>
                <p className="text-[#7f93bc] text-sm mb-8">Monnaies disponibles pour les environnements clients.</p>

                <div className="flex flex-wrap gap-3 mb-6">
                    {currencies.map(curr => (
                        <div key={curr.code} className="px-4 py-3 bg-indigo-500/15 text-indigo-300 font-bold rounded-xl border border-indigo-500/25 flex items-center gap-3">
                            <span className="text-lg">{curr.symbol}</span>
                            <div className="flex flex-col">
                                <span className="text-xs font-black">{curr.code}</span>
                                <span className="text-[9px] uppercase tracking-wide text-indigo-400/70">{curr.name}</span>
                            </div>
                            {currentUser.role === UserRole.SUPER_ADMIN && (
                                <button onClick={() => removeCurrency(curr.code)} className="ml-2 hover:text-rose-400 font-bold text-lg transition-colors">×</button>
                            )}
                        </div>
                    ))}
                </div>

                {currentUser.role === UserRole.SUPER_ADMIN && (
                    <div className="flex gap-4 max-w-2xl bg-[rgba(10,22,48,0.6)] p-4 rounded-2xl border border-[rgba(100,140,210,0.2)]">
                        <input
                            type="text"
                            placeholder="Code (ex: JPY)"
                            value={newCurrency.code}
                            onChange={(e) => setNewCurrency({...newCurrency, code: e.target.value.toUpperCase()})}
                            className="orion-admin-input w-24"
                            maxLength={3}
                        />
                        <input
                            type="text"
                            placeholder="Symbole (ex: ¥)"
                            value={newCurrency.symbol}
                            onChange={(e) => setNewCurrency({...newCurrency, symbol: e.target.value})}
                            className="orion-admin-input w-24"
                            maxLength={3}
                        />
                        <input
                            type="text"
                            placeholder="Nom (ex: Yen Japonais)"
                            value={newCurrency.name}
                            onChange={(e) => setNewCurrency({...newCurrency, name: e.target.value})}
                            className="orion-admin-input flex-1"
                        />
                        <button
                            onClick={addCurrency}
                            className="orion-primary-btn text-white px-5 py-2 rounded-xl font-black text-xs"
                        >
                            Ajouter
                        </button>
                    </div>
                )}
            </div>

            {/* MODALE ÉDITION PLAN */}
            {isEditingPlan && editingPlan && (
                <div className="orion-modal-backdrop fixed inset-0 z-[150] flex items-center justify-center p-6">
                    <div className="orion-modal-panel rounded-[2rem] p-8 w-full max-w-md shadow-2xl border border-[rgba(100,140,210,0.25)]">
                        <h3 className="text-xl font-black text-[#e0eaff] mb-6">Configuration Plan</h3>
                        <form onSubmit={handleSavePlan} className="space-y-4">
                            <div>
                                <label className="text-[10px] font-bold text-[#7f93bc] uppercase tracking-widest">ID Plan (Unique)</label>
                                <input required type="text" value={editingPlan.id} onChange={e => setEditingPlan({...editingPlan, id: e.target.value.toUpperCase()})} className="orion-admin-input w-full mt-1" />
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-[#7f93bc] uppercase tracking-widest">Label Affiché</label>
                                <input required type="text" value={editingPlan.label} onChange={e => setEditingPlan({...editingPlan, label: e.target.value})} className="orion-admin-input w-full mt-1" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-[10px] font-bold text-[#7f93bc] uppercase tracking-widest">Prix Mensuel</label>
                                    <input required type="number" value={editingPlan.priceMonthly} onChange={e => setEditingPlan({...editingPlan, priceMonthly: parseFloat(e.target.value)})} className="orion-admin-input w-full mt-1" />
                                </div>
                                <div>
                                    <label className="text-[10px] font-bold text-[#7f93bc] uppercase tracking-widest">Prix Annuel</label>
                                    <input required type="number" value={editingPlan.priceYearly} onChange={e => setEditingPlan({...editingPlan, priceYearly: parseFloat(e.target.value)})} className="orion-admin-input w-full mt-1" />
                                </div>
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-[#7f93bc] uppercase tracking-widest">Limite Utilisateurs</label>
                                <input required type="number" value={editingPlan.userLimit} onChange={e => setEditingPlan({...editingPlan, userLimit: parseInt(e.target.value)})} className="orion-admin-input w-full mt-1" />
                            </div>
                            <div className="flex gap-3 pt-4">
                                <button type="button" onClick={() => setIsEditingPlan(false)} className="flex-1 py-3 text-xs font-bold text-[#7f93bc] hover:text-[#e0eaff] transition-colors rounded-xl">Annuler</button>
                                <button type="submit" className="orion-primary-btn flex-1 py-3 text-white rounded-xl text-xs font-bold">Sauvegarder</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
      );
  }

  return <div className="text-[#7f93bc]">Vue en construction</div>;
};

export default SuperAdminDashboard;
